import { useCallback, useEffect, useRef, useState } from 'react';

interface VersionInfo {
    commitHash: string;
}

/** Poll interval. The deploy job rewrites version.json right after it invalidates CloudFront. */
const CHECK_INTERVAL_MS = 60 * 1000;

/**
 * Give up on a request well before the next tick. A stalled fetch or body read
 * would otherwise hold the re-entrancy gate shut and stop the polling for good.
 */
const REQUEST_TIMEOUT_MS = 10 * 1000;

/**
 * Watches `/version.json` for a commit hash that differs from `currentVersion`, the
 * one this bundle was built with, i.e. a deploy happened while the tab stayed open.
 *
 * The file is written by each app's deploy job (`apps/<app>/gitlab-ci/deploy.gitlab-ci.yml`)
 * and served with `no-cache`, so a fetch always reaches S3. Local builds have no
 * `VITE_GIT_COMMIT_HASH`, so pass it through as is: an empty hash keeps the check off.
 */
export function useVersionCheck(currentVersion: string | undefined): boolean {
    const [isUpdateAvailable, setUpdateAvailable] = useState(false);
    // Re-entrancy gate only — never read while rendering, so a ref avoids a re-render.
    const isCheckingRef = useRef(false);

    const checkForUpdate = useCallback(async () => {
        if (isCheckingRef.current) return;
        isCheckingRef.current = true;

        try {
            const response = await fetch('/version.json', {
                cache: 'no-store',
                headers: { 'Cache-Control': 'no-cache' },
                // Covers the body read below too: an abort rejects the pending json().
                signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
            });
            if (!response.ok) return;

            // A deploy replaces version.json mid-flight, so a fetch can land on a
            // partial or missing body. Nothing to do — the next tick sees the new file.
            const versionInfo = (await response.json()) as Partial<VersionInfo>;
            if (versionInfo.commitHash && versionInfo.commitHash !== currentVersion) {
                setUpdateAvailable(true);
            }
        } catch {
            // Offline, timed out, or the partial-body case above. Retry on the next tick.
        } finally {
            isCheckingRef.current = false;
        }
    }, [currentVersion]);

    useEffect(() => {
        // Without a build-time hash every deploy would look like an update.
        if (!currentVersion || isUpdateAvailable) return;

        const interval = setInterval(() => void checkForUpdate(), CHECK_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [checkForUpdate, currentVersion, isUpdateAvailable]);

    return isUpdateAvailable;
}
